/**
 * Cookie Consent Banner
 * Multi-jurisdictional compliant (GDPR, CCPA, PDPA)
 * Shows on first visit or when consent preferences don't exist
 */

import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Settings, Cookie } from 'lucide-react';
import {
  hasConsentChoice,
  acceptAllConsent,
  rejectAllConsent
} from '../utils/consentManager';
import CookiePreferencesModal from './modals/CookiePreferencesModal';

export default function CookieConsentBanner() {
  const [isVisible, setIsVisible] = useState(false);
  const [showPreferences, setShowPreferences] = useState(false);

  useEffect(() => {
    // Show banner if user hasn't made a consent choice yet
    if (!hasConsentChoice()) {
      // Small delay to avoid overwhelming user immediately
      const timer = setTimeout(() => {
        setIsVisible(true);
      }, 1000);

      return () => clearTimeout(timer);
    }
  }, []);

  const handleAcceptAll = () => {
    acceptAllConsent();
    setIsVisible(false);
  };

  const handleRejectAll = () => {
    rejectAllConsent();
    setIsVisible(false);
  };

  const handleCustomize = () => {
    setShowPreferences(true);
  };

  const handlePreferencesSaved = () => {
    setShowPreferences(false);
    setIsVisible(false);
  };

  if (!isVisible) {
    return null;
  }

  return (
    <>
      {/* Overlay backdrop */}
      <div className="fixed inset-0 bg-black/20 z-40" aria-hidden="true" />

      {/* Banner */}
      <div
        className="fixed bottom-0 left-0 right-0 z-50 bg-white shadow-2xl border-t-4 border-primary animate-slide-up"
        role="dialog"
        aria-labelledby="cookie-consent-title"
        aria-describedby="cookie-consent-description"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex flex-col lg:flex-row items-start lg:items-center gap-6">
            {/* Icon and Content */}
            <div className="flex items-start gap-4 flex-1">
              <div className="flex-shrink-0">
                <Cookie className="w-8 h-8 text-primary" aria-hidden="true" />
              </div>
              <div className="flex-1">
                <h2
                  id="cookie-consent-title"
                  className="text-lg font-semibold text-gray-900 mb-2"
                >
                  Your Privacy Choices
                </h2>
                <p
                  id="cookie-consent-description"
                  className="text-sm text-gray-600 leading-relaxed"
                >
                  We use cookies and similar technologies to enhance your browsing experience,
                  analyze site traffic, and understand where our visitors are coming from.
                  You can choose which cookies to accept. We respect your privacy and comply
                  with GDPR, CCPA, and other privacy regulations.
                  {' '}
                  <Link
                    to="/privacy-policy"
                    className="text-primary hover:text-primary-dark underline font-medium"
                  >
                    Privacy Policy
                  </Link>
                  {' • '}
                  <Link
                    to="/cookie-policy"
                    className="text-primary hover:text-primary-dark underline font-medium"
                  >
                    Cookie Policy
                  </Link>
                </p>
              </div>
            </div>

            {/* Actions */}
            <div className="flex flex-col sm:flex-row gap-3 w-full lg:w-auto">
              <button
                onClick={handleCustomize}
                className="inline-flex items-center justify-center gap-2 px-6 py-3 border-2 border-gray-300 text-gray-700 font-medium rounded-lg hover:border-gray-400 hover:bg-gray-50 transition-colors focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2"
                aria-label="Customize cookie preferences"
              >
                <Settings className="w-4 h-4" aria-hidden="true" />
                <span>Customize</span>
              </button>

              <button
                onClick={handleRejectAll}
                className="inline-flex items-center justify-center px-6 py-3 border-2 border-gray-300 text-gray-700 font-medium rounded-lg hover:border-gray-400 hover:bg-gray-50 transition-colors focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2"
                aria-label="Reject all non-essential cookies"
              >
                Essential Only
              </button>

              <button
                onClick={handleAcceptAll}
                className="inline-flex items-center justify-center px-6 py-3 bg-primary text-white font-medium rounded-lg hover:bg-primary-dark transition-colors focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 shadow-lg"
                aria-label="Accept all cookies"
              >
                Accept All
              </button>
            </div>
          </div>

          {/* Additional compliance info */}
          <div className="mt-4 pt-4 border-t border-gray-200">
            <p className="text-xs text-gray-500">
              By clicking "Accept All", you consent to our use of cookies for analytics
              and marketing purposes. You can change your preferences at any time.
              {' '}
              <strong>For California residents:</strong> This includes our use of cookies
              for advertising purposes, which may constitute "selling" or "sharing" personal
              information under CCPA.
            </p>
          </div>
        </div>
      </div>

      {/* Cookie Preferences Modal */}
      {showPreferences && (
        <CookiePreferencesModal
          isOpen={showPreferences}
          onClose={() => setShowPreferences(false)}
          onSave={handlePreferencesSaved}
        />
      )}

      <style>{`
        @keyframes slide-up {
          from {
            transform: translateY(100%);
            opacity: 0;
          }
          to {
            transform: translateY(0);
            opacity: 1;
          }
        }

        .animate-slide-up {
          animation: slide-up 0.3s ease-out;
        }
      `}</style>
    </>
  );
}
